import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Button, Modal, SelectLabel, ToastContext, ToggleLabel, Status } from '@weezevent/nacre';
import { capitalizeFirstLetter, translateLocale } from '../../../../attendee/detail/components/utils';
import { ModerationAttendee, ModerationTransitionValidation } from '../../../../../models';
import { colors } from '../../../../../constants/moderation';
import { ActionComment } from './components/Comments';
import { Validations } from './components/Validations';
import { Loader } from 'semantic-ui-react';

import Style from './moderationModal.module.css';

export default function ModerationModal({ open, setOpenModal, attendee, organization, flowId, handleNextAttendee, nextAttendeeLoading, transitions, loadingTransitions, order }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [transitionId, setTransitionId] = React.useState(null);
    const [transitionActions, setTransitionActions] = React.useState([]);
    const [emailAction, setEmailAction] = React.useState(null);
    const [createAttendeeActions, setCreateAttendeeActions] = React.useState([]);
    const [commentChoice, setCommentChoice] = React.useState(null);
    const [actionIds, setActionIds] = React.useState([]);
    const [actionCommentOnTransition, setActionCommentOnTransition] = React.useState(null);
    const [validationsInputs, setValidationsInputs] = React.useState([]);
    const currentFlowId = flowId || attendee.moderation_flow_id;
    const locale = i18n.language;
    const history = useHistory();

    const [transitionValidations, loadingTransitionValidations] = ModerationTransitionValidation.useApiModel(
        {
            organizationId: organization.pk,
            flowId,
            transitionId,
            launch: Boolean(transitionId),
            onSuccess: response => {
                setValidationsInputs(response.map(it => ({ ...it.object, value: false })));
            }
        },
        [organization, flowId, transitionId]
    );

    const transitionsOptions = React.useMemo(() => {
        if (!transitions || loadingTransitions) {
            return [];
        }

        return transitions?.map(transition => {
            const transitionName = transition.name ? transition.name : `${transition.source_status.name} > ${transition.destination_status.name}`;

            return {
                text: (
                    <div className={Style['moderation-modal-transitions-select-option']}>
                        {transitionName} <Status color={colors[transition.destination_status.stage]} text={transition.destination_status.name} />
                    </div>
                ),
                id: transition.id,
                key: transition.id,
                value: transition.id,
                actions: transition.actions,
                disabled: !transition.is_available
            };
        });
    }, [transitions, loadingTransitions]);

    const isCommentRequired = React.useMemo(() => {
        return emailAction && emailAction.comment_required && !actionCommentOnTransition;
    }, [emailAction, actionCommentOnTransition]);

    const hasPendingValidations = React.useMemo(() => {
        if (!transitionId || loadingTransitionValidations || !validationsInputs.length > 0) {
            return false;
        }
        return validationsInputs.some(it => it.checkbox_mandatory && !it.value);
    }, [transitionId, loadingTransitionValidations, validationsInputs]);

    const handleSubmit = React.useCallback(
        (isNextAttendeeCta = false) => {
            const actions = actionIds.map(id => ({ id }));
            let validations = validationsInputs.map(({ id, value }) => ({ id, value }));
            transitionActions.forEach(action => {
                if (action.type === 'email_action') {
                    const emailActionIndex = actionIds.findIndex(actionId => actionId === action.id);

                    if (emailActionIndex && actionCommentOnTransition) {
                        transitionActions[emailActionIndex]['data'] = {
                            comment: actionCommentOnTransition
                        };
                        if (commentChoice) {
                            transitionActions[emailActionIndex].data['label'] = {
                                id: commentChoice.id,
                                label: commentChoice.label
                            };
                        }
                    }
                }
            });
            ModerationAttendee.triggerTransition(
                {
                    transition: transitionId,
                    actions,
                    validations
                },
                {
                    organizationId: organization.id,
                    attendeeId: attendee.id,
                    flowId: currentFlowId
                }
            )
                .then(() => {
                    if (isNextAttendeeCta) {
                        handleNextAttendee();
                    } else {
                        setCreateAttendeeActions([]);
                        setEmailAction(null);
                        if (flowId) {
                            history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/moderation_attendees`);
                        } else {
                            history.push(`/ticket/O${organization.id}/moderation/moderation_attendees`);
                        }
                    }
                    toast.success(t(`ticket.moderation.attendee.moderationModal.toastSuccess`));
                })
                .catch(error => {
                    toast.error(t(`ticket.moderation.attendee.moderationModal.toastError`));
                });
        },
        [organization, attendee, currentFlowId, transitionId, commentChoice, actionCommentOnTransition, validationsInputs, actionIds, transitionActions]
    );

    if (!open) {
        return null;
    }

    return (
        <Modal
            size="large"
            className={Style['moderation-modal']}
            open={open}
            onClose={() => {
                setEmailAction(null);
                setCreateAttendeeActions([]);
                setOpenModal(false);
            }}
            title={
                <div className={Style['moderation-modal-titleContainer']}>
                    {t('ticket.moderation.attendee.moderationModal.title')}
                    <Status color={colors[attendee?.status.stage]} text={attendee?.status.name} />
                </div>
            }
            subtitle={attendee.attendeeResolvedName}
            allowExternalClosing={false}
            customFooter={
                <Button.Group className={Style['moderation-modal-groupContainer']}>
                    <Button label={t('ticket.common.action.back')} onClick={() => setOpenModal(false)} disabled={nextAttendeeLoading} />
                    <Button
                        primary
                        inverted
                        label={t('ticket.moderation.attendee.moderationModal.ctaSaveQuit')}
                        onClick={() => handleSubmit()}
                        disabled={hasPendingValidations || isCommentRequired || nextAttendeeLoading}
                    />
                    <Button
                        primary
                        label={
                            nextAttendeeLoading ? <Loader className={Style['loader-count-loader']} active /> : t('ticket.moderation.attendee.moderationModal.saveNextAttendeeCTA')
                        }
                        disabled={hasPendingValidations || isCommentRequired}
                        onClick={() => handleSubmit(true)}
                    />
                </Button.Group>
            }
        >
            <SelectLabel
                useNew
                label={t('ticket.moderation.attendee.moderationModal.transitionLabel')}
                value={transitionId}
                options={transitionsOptions}
                placeholder={t('ticket.common.action.select')}
                className={Style['moderation-modal-transitionSelect']}
                onChange={(_, { id, actions }) => {
                    setTransitionId(id);
                    setTransitionActions(actions);
                    const foundEmailAction = actions.find(action => action.type === 'email_action') || null;
                    setEmailAction(foundEmailAction);
                    setCreateAttendeeActions(actions.filter(action => action.type === 'attendee_creation_action'));
                }}
            />

            {emailAction && (
                <ActionComment
                    locale={order?.locale}
                    action={emailAction}
                    comment={actionCommentOnTransition}
                    onChange={setActionCommentOnTransition}
                    choice={commentChoice}
                    setChoice={setCommentChoice}
                />
            )}
            {transitionId && <Validations validationsInputs={validationsInputs} setValidationsInputs={setValidationsInputs} transitionValidations={transitionValidations} />}
            {emailAction && (
                <ToggleLabel
                    className={Style['moderation-modal-notify']}
                    inlinePosition="left"
                    label={t('ticket.moderation.attendee.moderationModal.notifyLabel')}
                    checked={emailAction.optional_value || emailAction.optional_value === null}
                    disabled={emailAction.optional_value === null}
                    onChange={(_, { checked }) => {
                        if (checked) {
                            setActionIds(prev => [...prev, emailAction.id]);
                        } else {
                            setActionIds(prev => prev.filter(id => id !== emailAction.id));
                        }
                    }}
                />
            )}
            {createAttendeeActions?.length > 0 && (
                <>
                    {createAttendeeActions.map(createAction => (
                        <ToggleLabel
                            key={createAction.name} // always add a key when mapping
                            className={Style['moderation-modal-notify']}
                            inlinePosition="left"
                            label={createAction.name}
                            checked={createAction.optional_value || createAction.optional_value === null}
                            disabled={createAction.optional_value === null}
                            onChange={(_, { checked }) => {
                                if (checked) {
                                    setActionIds(prev => [...prev, createAction.id]);
                                } else {
                                    setActionIds(prev => prev.filter(id => id !== createAction.id));
                                }
                            }}
                        />
                    ))}
                </>
            )}
            <p>
                {t('ticket.moderation.attendee.moderationModal.languageLabel')}
                <b>
                    {capitalizeFirstLetter(translateLocale(locale, order.locale.split('_')[1]))} ({order.locale.split('_')[1].toUpperCase()})
                </b>
            </p>
        </Modal>
    );
}
